<template>
    <teleport to="body">
        <div class="nickname-dialog-overlay" @click.self="emit('cancel')">
            <section ref="dialog" class="nickname-dialog" role="dialog" aria-modal="true"
                :aria-labelledby="titleId" :aria-describedby="hintId" @keydown="onKeydown">
                <header class="nickname-dialog-header">
                    <h2 :id="titleId">Đặt tên gợi nhớ</h2>
                    <button type="button" class="nickname-dialog-close" aria-label="Đóng" :disabled="saving"
                        @click="emit('cancel')">×</button>
                </header>

                <div class="nickname-dialog-body">
                    <UserProfile :data="{ id: userId, title: officialName, image: avatarUrl }"
                        width="76px" thumbnail="120x120" :showDot="false" />
                    <p>Hãy đặt cho <strong>{{ officialName }}</strong> một cái tên dễ nhớ.</p>
                    <p :id="hintId" class="nickname-dialog-hint">Chỉ hiển thị với riêng bạn.</p>
                    <label :for="inputId" class="nickname-dialog-label">Tên gợi nhớ</label>
                    <input :id="inputId" ref="input" :value="modelValue" type="text" maxlength="50"
                        :placeholder="officialName" :disabled="saving" autocomplete="off"
                        @input="emit('update:modelValue', $event.target.value)"
                        @keydown.enter.prevent="emit('confirm')" />
                    <p v-if="error" class="nickname-dialog-error" role="alert">{{ error }}</p>
                    <button v-if="nickname" type="button" class="nickname-dialog-remove" :disabled="saving"
                        @click="emit('remove')">Xóa tên gợi nhớ</button>
                </div>

                <footer class="nickname-dialog-actions">
                    <button type="button" class="nickname-dialog-cancel" :disabled="saving"
                        @click="emit('cancel')">Hủy</button>
                    <button type="button" class="nickname-dialog-confirm" :disabled="saving"
                        @click="emit('confirm')">{{ saving ? 'Đang lưu…' : 'Xác nhận' }}</button>
                </footer>
            </section>
        </div>
    </teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, useId } from 'vue';
import UserProfile from '@/components/atom/UserProfile/UserProfile.vue';

defineProps({
    userId: { type: [String, Number], default: '' },
    officialName: { type: String, default: '' },
    avatarUrl: { type: String, default: '' },
    modelValue: { type: String, default: '' },
    nickname: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    error: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'remove']);
const uid = useId();
const titleId = `${uid}-title`;
const hintId = `${uid}-hint`;
const inputId = `${uid}-input`;
const dialog = ref(null);
const input = ref(null);
let previousFocus;

function onKeydown(event) {
    if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        emit('cancel');
    }
    if (event.key !== 'Tab') return;
    const controls = [...dialog.value.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
}

onMounted(async () => {
    previousFocus = document.activeElement;
    document.body.classList.add('nickname-dialog-open');
    await nextTick();
    input.value?.focus();
    input.value?.select();
});

onBeforeUnmount(() => {
    document.body.classList.remove('nickname-dialog-open');
    previousFocus?.focus?.();
});
</script>

<style scoped>
:global(body.nickname-dialog-open) { overflow: hidden; }
.nickname-dialog-overlay { position: fixed; inset: 0; z-index: 110000; display: grid; place-items: center; padding: 20px; background: rgba(14, 27, 45, .55); }
.nickname-dialog { width: min(440px, 100%); max-height: calc(100dvh - 40px); overflow: auto; border-radius: 12px; background: #fff; box-shadow: 0 22px 65px rgba(7, 28, 57, .28); color: #1d2c41; }
.nickname-dialog-header { display: flex; align-items: center; justify-content: space-between; min-height: 56px; padding: 12px 20px; border-bottom: 1px solid #e7edf4; }
.nickname-dialog-header h2 { margin: 0; font-size: 17px; font-weight: 700; }
.nickname-dialog-close { width: 32px; height: 32px; border: 0; border-radius: 7px; background: transparent; color: #65738a; font-size: 24px; line-height: 1; cursor: pointer; }
.nickname-dialog-close:hover { background: #f0f4f8; }
.nickname-dialog-body { display: flex; flex-direction: column; align-items: center; padding: 22px 28px 8px; text-align: center; }
.nickname-dialog-body > p { margin: 17px 0 0; line-height: 1.45; }
.nickname-dialog-body > p strong { font-weight: 700; }
.nickname-dialog-body .nickname-dialog-hint { margin-top: 2px; color: #65738a; font-size: 13px; }
.nickname-dialog-label { align-self: flex-start; margin: 20px 0 7px; color: #43536b; font-size: 13px; font-weight: 600; }
.nickname-dialog-body input { width: 100%; height: 42px; padding: 0 12px; border: 1px solid #b7c9df; border-radius: 7px; background: #fff; color: #1d2c41; outline: none; text-align: left; }
.nickname-dialog-body input:focus { border-color: #0871db; box-shadow: 0 0 0 3px rgba(8, 113, 219, .14); }
.nickname-dialog-body .nickname-dialog-error { align-self: stretch; margin: 8px 0 0; color: #c93838; font-size: 12px; text-align: left; }
.nickname-dialog-remove { align-self: flex-start; min-height: 34px; margin-top: 7px; padding: 0; border: 0; background: transparent; color: #c93838; font-size: 12px; cursor: pointer; }
.nickname-dialog-remove:hover { text-decoration: underline; }
.nickname-dialog-actions { display: flex; justify-content: flex-end; gap: 10px; padding: 18px 20px 20px; }
.nickname-dialog-actions button { min-height: 40px; padding: 0 18px; border: 1px solid #d6e0eb; border-radius: 7px; font-weight: 600; cursor: pointer; }
.nickname-dialog-cancel { background: #f2f5f8; color: #43536b; }
.nickname-dialog-confirm { border-color: #0871db !important; background: #0871db; color: #fff; }
.nickname-dialog-actions button:hover:not(:disabled) { filter: brightness(.96); }
.nickname-dialog button:disabled, .nickname-dialog input:disabled { opacity: .55; cursor: wait; }
.nickname-dialog button:focus-visible, .nickname-dialog input:focus-visible { outline: 2px solid #0871db; outline-offset: 2px; }
@media (max-width: 480px) { .nickname-dialog-overlay { padding: 12px; } .nickname-dialog-body { padding: 20px 20px 6px; } }
</style>
